import AssignmentLateOutlined from "@mui/icons-material/AssignmentLateOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import GavelOutlined from "@mui/icons-material/GavelOutlined";
import PhoneOutlined from "@mui/icons-material/PhoneOutlined";
import RateReviewOutlined from "@mui/icons-material/RateReviewOutlined";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { StatusChip, type StatusChipColor } from "components/StatusChip";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { ManifestComplianceEscalation, SubmissionStatus } from "generated-graphql/graphql";
import { type DiscrepancySectionKey } from "routes/Customer/Waste/Manifests/DiscrepancyReviewDialog";
import {
  EManifestCell,
  type EManifestCellInput,
} from "routes/Customer/Waste/Manifests/EManifestCell";
import { MANIFEST_BLOCK_REASON_LABELS } from "routes/Customer/Waste/Manifests/manifestBlockReasonDisplay";

import { assigneeLabel, type ManifestQuickLink, type ManifestReviewRow } from "./api";

/**
 * The "Blocked by" column: why a bottom-sorted manifest is stuck, as plain text (the
 * tooltip carries the note, when, and who). Blank for workable manifests.
 */
export function ManifestBlockedCell({ row }: { row: ManifestReviewRow }) {
  if (!row.blockedReason) return null;
  const tooltip = [
    row.blockedNote,
    row.blockedAt ? `Blocked ${getUtcDisplayDate(row.blockedAt)}` : null,
    row.blockedBy ? `by ${assigneeLabel(row.blockedBy)}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <Tooltip title={tooltip}>
      <span>{MANIFEST_BLOCK_REASON_LABELS[row.blockedReason] ?? row.blockedReason}</span>
    </Tooltip>
  );
}

const DOC_REVIEW_STYLE: Record<string, { label: string; color: StatusChipColor }> = {
  NEEDS_REVIEW: { label: "Needs Review", color: "warning" },
  // Reviewed source with a newer unprocessed version — refresh to re-process.
  NEEDS_REFRESH: { label: "New Version", color: "warning" },
  REVIEWED: { label: "Reviewed", color: "success" },
  NOT_PROCESSED: { label: "Not Processed", color: "default" },
};

/** The Document Review column: one chip reflecting the manifest's document state. */
export function DocumentReviewCell({
  state,
  onClick,
}: {
  state: string | null | undefined;
  onClick?: () => void;
}) {
  if (!state) return null;
  const style = DOC_REVIEW_STYLE[state];
  if (!style) return null;
  return (
    <StatusChip
      variant="outlined"
      color={style.color}
      label={style.label}
      clickable={Boolean(onClick)}
      onClick={
        onClick
          ? (event) => {
              event.stopPropagation();
              onClick();
            }
          : undefined
      }
    />
  );
}

/** Map the row's escalation string to the customer cell's enum (no cast). */
function toEscalation(value: string | null | undefined): ManifestComplianceEscalation | null {
  if (value === "OVERDUE_60") return ManifestComplianceEscalation.Overdue_60;
  if (value === "OVERDUE_45") return ManifestComplianceEscalation.Overdue_45;
  return null;
}

/**
 * Adapt the exception-report submission status to the shape EManifestCell reads.
 * That cell only distinguishes "filed" (FilingComplete) from "started" (any other
 * existing submission), so non-filed statuses collapse to InProgress.
 */
function toExceptionSubmission(
  status: string | null | undefined
): { status: SubmissionStatus } | null {
  if (!status) return null;
  if (status === SubmissionStatus.FilingComplete)
    return { status: SubmissionStatus.FilingComplete };
  return { status: SubmissionStatus.InProgress };
}

/** The e-Manifest Compliance column — reuses the customer manifest grid's cell. */
export function ManifestReviewEManifestCell({
  row,
  onClick,
}: {
  row: ManifestReviewRow;
  onClick?: () => void;
}) {
  const input: EManifestCellInput = {
    isEManifest: row.isEManifest,
    isUniformHazardousManifest: row.isUniformHazardousManifest,
    daysSinceShipment: row.daysSinceShipment,
    eManifestComplianceEscalation: toEscalation(row.eManifestComplianceEscalation),
    transporterContactedAt: row.transporterContactedAt,
    exceptionReportSubmission: toExceptionSubmission(row.exceptionReportSubmissionStatus),
  };
  const cell = <EManifestCell input={input} />;
  if (!onClick) return cell;
  return (
    <Box
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      sx={{ cursor: "pointer" }}
    >
      {cell}
    </Box>
  );
}

// All @mui/icons-material icons share this component type; reuse one to type the
// field without importing the type from the (bundle-heavy) icons barrel.
type IconComponent = typeof RateReviewOutlined;

type StaffActionMeta = {
  key: string;
  Icon: IconComponent;
  /** Resolves the action's distinct color from the theme (used for the icon + tint). */
  color: (theme: Theme) => string;
  /** Whether this manifest currently needs this action. */
  required: (row: ManifestReviewRow) => boolean;
  /** Tooltip line shown only while the action is required. */
  describe: (row: ManifestReviewRow) => string;
  /** In-place link opened when an active icon is clicked (mirrors the column links). */
  quickLink: (row: ManifestReviewRow) => ManifestQuickLink;
};

/** Open the discrepancy dialog on a given section for this row. */
function discrepancyLink(row: ManifestReviewRow, tab: DiscrepancySectionKey): ManifestQuickLink {
  return {
    kind: "discrepancies",
    processedManifestId: row.processedManifestId,
    tenantId: row.tenantId,
    tab,
    manifestTrackingNumber: row.manifestTrackingNumber,
  };
}

/** Data quality rows still open: filed as PENDING, or reopened to UNRESOLVED. */
function openDataQuality(row: ManifestReviewRow): number {
  return (row.dataQualityUnresolved ?? 0) + (row.dataQualityPending ?? 0);
}

/** EPA correction rows still open: filed as PENDING, or UNRESOLVED from before. */
function openEpaCorrections(row: ManifestReviewRow): number {
  return (row.epaUnresolved ?? 0) + (row.epaPending ?? 0);
}

// One entry per outstanding-action facet (mirrors the staffActions filter +
// STAFF_ACTION_SQL, so the discrepancy actions stay dark on a blocked manifest). Every icon is ALWAYS rendered; only required ones light up in
// their own color (the rest stay pale grey). Each color is distinct so the lit icons
// are unambiguous: blue = doc review, orange = unreviewed data source conflicts, teal = data
// quality, red = EPA corrections, green = vendor contact, purple = exception report.
const STAFF_ACTIONS_META: StaffActionMeta[] = [
  {
    key: "docReview",
    Icon: RateReviewOutlined,
    color: (theme) => theme.palette.info.main,
    required: (row) => row.documentReviewState === "NEEDS_REVIEW",
    describe: () => "Document needs review",
    quickLink: (row) => ({
      kind: "docReview",
      processedManifestId: row.processedManifestId,
      tenantId: row.tenantId,
      documentIds: row.documentIds,
    }),
  },
  {
    key: "discrepancyReview",
    Icon: ErrorOutline,
    color: (theme) => theme.palette.warning.main,
    required: (row) => !row.blockedReason && (row.encampUnresolved ?? 0) > 0,
    describe: (row) => `Data source conflicts to review (${row.encampUnresolved})`,
    quickLink: (row) => discrepancyLink(row, "field"),
  },
  {
    key: "dataQualityReview",
    Icon: FactCheckOutlined,
    color: () => "#00897b",
    required: (row) => !row.blockedReason && openDataQuality(row) > 0,
    describe: (row) => `Open data quality issues (${openDataQuality(row)})`,
    quickLink: (row) => discrepancyLink(row, "fieldIssue"),
  },
  {
    key: "epaCorrections",
    Icon: GavelOutlined,
    color: (theme) => theme.palette.error.main,
    required: (row) => !row.blockedReason && openEpaCorrections(row) > 0,
    describe: (row) => `EPA corrections to resolve (${openEpaCorrections(row)})`,
    quickLink: (row) => discrepancyLink(row, "epaCorrection"),
  },
  {
    key: "vendorContact",
    Icon: PhoneOutlined,
    color: (theme) => theme.palette.success.main,
    // Drops once the transporter has been contacted (the action is handled).
    required: (row) =>
      row.eManifestComplianceEscalation === "OVERDUE_45" && !row.transporterContactedAt,
    describe: () => "Vendor contact required (45 days overdue)",
    quickLink: (row) => discrepancyLink(row, "eManifestMissing"),
  },
  {
    key: "exceptionReport",
    Icon: AssignmentLateOutlined,
    color: () => "#8e24aa",
    // Drops once an exception submission exists (started or filed = handled).
    required: (row) =>
      row.eManifestComplianceEscalation === "OVERDUE_60" && !row.exceptionReportSubmissionStatus,
    describe: () => "Exception report required (60 days overdue)",
    quickLink: (row) => discrepancyLink(row, "eManifestMissing"),
  },
];

/**
 * Staff Actions Required column: a fixed row of circled icons, one per action facet.
 * Every icon is always shown; a required action lights up in its own color while the
 * rest stay pale grey (no hover/highlight — purely a status readout). The tooltip
 * lists ONLY the actions actually required, so a manifest with no open work shows
 * five grey icons and no tooltip.
 */
export function StaffActionsRequiredCell({
  row,
  onOpenQuickLink,
}: {
  row: ManifestReviewRow;
  onOpenQuickLink?: (link: ManifestQuickLink) => void;
}) {
  const required = STAFF_ACTIONS_META.filter((action) => action.required(row));
  const tooltip =
    required.length > 0 ? (
      <Stack spacing={0.25}>
        {required.map((action) => (
          <Typography key={action.key} variant="caption">
            {action.describe(row)}
          </Typography>
        ))}
      </Stack>
    ) : (
      ""
    );
  return (
    <Tooltip title={tooltip} placement="top" enterDelay={400}>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ py: 0.5 }}>
        {STAFF_ACTIONS_META.map((action) => {
          const active = action.required(row);
          // Active icons open the same surface as the matching column link.
          const clickable = active && Boolean(onOpenQuickLink);
          return (
            <Box
              key={action.key}
              role={clickable ? "button" : undefined}
              aria-label={clickable ? action.describe(row) : undefined}
              onClick={
                clickable
                  ? (event) => {
                      event.stopPropagation();
                      onOpenQuickLink?.(action.quickLink(row));
                    }
                  : undefined
              }
              sx={(theme) => {
                const tint = action.color(theme);
                return {
                  width: 26,
                  height: 26,
                  flexShrink: 0,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: clickable ? "pointer" : "default",
                  border: "1.5px solid",
                  borderColor: active ? tint : theme.palette.grey[300],
                  color: active ? tint : theme.palette.grey[400],
                  // Light tint fill in the action's color when active (matches the
                  // outlined discrepancy/correction bubbles); transparent when inactive.
                  backgroundColor: active ? alpha(tint, 0.14) : "transparent",
                };
              }}
            >
              <action.Icon sx={{ fontSize: 16, color: "inherit" }} />
            </Box>
          );
        })}
      </Stack>
    </Tooltip>
  );
}
